import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { SharedDriveModel, Restrictions } from '../../../../../../../types';
import { UserKeys, updateSharedDrive } from '../../../../../../api/users-api';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useStore } from '../../../../../state/zustand-store';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Messages } from '../../../../../../utils/message-constants';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../../../utils/feature-flags';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface SharedDrivesSettingsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDrive: SharedDriveModel;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  changeLogRoles: string[];
  primaryDomain: string;
}

export default function SharedDrivesSettingsDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedDrive,
  setSelectedDrive,
  primaryDomain,
}: SharedDrivesSettingsDialogProps) {
  const location = useLocation();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { features, adminEmail, currentTier } = useUserSettings();

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const [searchParams] = useSearchParams();
  // if hasUserEmail is true, then you're in drive explorer
  const hasUserEmail = searchParams.has('userEmail');
  let userEmail = searchParams.get('userEmail') || '';

  if (!hasUserEmail) {
    // then you're in user details
    const userRoute = location.pathname.split('/')[1];
    const userArray = userRoute.split('-');
    const pathEmail = userArray.slice(1).join('-');

    userEmail = pathEmail;
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<SharedDriveModel>) =>
      updateSharedDrive(userEmail, data, changeLogMessage),
    onSuccess: (_returnedData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('The shared drive settings'),
      });

      function updateDriveData(sData: SharedDriveModel[] | undefined) {
        if (!sData) return;
        return sData.map((drive) =>
          drive.id === selectedDrive.id
            ? { ...drive, restrictions: data.restrictions }
            : drive
        );
      }

      if (currentTier === 'enterprise') {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDrivesIndexedPage],
        });
      } else if (!isFeatureEnabled('showPaginatedSharedDrives', features)) {
        queryClient.setQueryData(
          [UserKeys.SharedDrivesForDomain],
          updateDriveData
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDrivesForDomainPage],
        });
      }

      const queries = queryClient.getQueriesData<SharedDriveModel[]>({
        queryKey: [UserKeys.SharedDrivesForUser],
      });

      queries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, updateDriveData(data));
      });

      setChangeLogMessage('');
      setSelectedDrive(null);
      reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the shared drive settings'
        ),
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.shared_drives.update_settings, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(
          analytics.drive.drive_explorer.shared_drives.update_settings,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          }
        );
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives
            .update_settings,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }

      setOpenChangeLogDialog(false);
    },
  });

  const {
    restrictions: {
      adminManagedRestrictions,
      copyRequiresWriterPermission,
      domainUsersOnly,
      driveMembersOnly,
      sharingFoldersRequiresOrganizerPermission,
    },
  } = selectedDrive;

  const {
    reset,
    handleSubmit,
    control,
    getValues,
    formState: { isDirty },
  } = useForm<Restrictions>({
    defaultValues: {
      adminManagedRestrictions: !adminManagedRestrictions,
      copyRequiresWriterPermission: !copyRequiresWriterPermission,
      domainUsersOnly: !domainUsersOnly,
      driveMembersOnly: !driveMembersOnly,
      sharingFoldersRequiresOrganizerPermission:
        !sharingFoldersRequiresOrganizerPermission,
    },
  });

  const onSubmit: SubmitHandler<Restrictions> = (data) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.CanUpdateSharedDriveSettings.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate({
        id: selectedDrive.id,
        restrictions: {
          adminManagedRestrictions: !data.adminManagedRestrictions,
          copyRequiresWriterPermission: !data.copyRequiresWriterPermission,
          domainUsersOnly: !data.domainUsersOnly,
          driveMembersOnly: !data.driveMembersOnly,
          sharingFoldersRequiresOrganizerPermission:
            !data.sharingFoldersRequiresOrganizerPermission,
        },
      });
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSelectedDrive(null);
        handleClose();
      }}
      title="Shared Drive Settings"
      actingAs={adminEmail}
      dialogContent={
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormControlLabel
            control={
              <Controller
                name={'adminManagedRestrictions'}
                control={control}
                render={({ field: props }) => {
                  return (
                    <Checkbox
                      {...props}
                      checked={props.value}
                      onChange={(e) => props.onChange(e.target.checked)}
                    />
                  );
                }}
              />
            }
            label={'Allow managers to modify shared drive settings'}
          />
          <Stack sx={{ my: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>Access</Typography>
            <FormControlLabel
              control={
                <Controller
                  name={'domainUsersOnly'}
                  control={control}
                  render={({ field: props }) => {
                    return (
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => props.onChange(e.target.checked)}
                      />
                    );
                  }}
                />
              }
              label={`Allow people outside of ${primaryDomain} to access files`}
            />
            <FormControlLabel
              control={
                <Controller
                  name={'driveMembersOnly'}
                  control={control}
                  render={({ field: props }) => {
                    return (
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => props.onChange(e.target.checked)}
                      />
                    );
                  }}
                />
              }
              label={`Allow people who aren't shared drive members to access files`}
            />
          </Stack>

          <Stack sx={{ my: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              Role permissions
            </Typography>
            <FormControlLabel
              control={
                <Controller
                  name={'sharingFoldersRequiresOrganizerPermission'}
                  control={control}
                  render={({ field: props }) => {
                    return (
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => props.onChange(e.target.checked)}
                      />
                    );
                  }}
                />
              }
              label={`Allow content managers to share folders`}
            />
            <FormControlLabel
              control={
                <Controller
                  name={'copyRequiresWriterPermission'}
                  control={control}
                  render={({ field: props }) => {
                    return (
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => props.onChange(e.target.checked)}
                      />
                    );
                  }}
                />
              }
              label={`Allow views and commenters to download, print, and copy files`}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate({
                id: selectedDrive.id,
                restrictions: {
                  adminManagedRestrictions: !getValues(
                    'adminManagedRestrictions'
                  ),
                  copyRequiresWriterPermission: !getValues(
                    'copyRequiresWriterPermission'
                  ),
                  domainUsersOnly: !getValues('domainUsersOnly'),
                  driveMembersOnly: !getValues('driveMembersOnly'),
                  sharingFoldersRequiresOrganizerPermission: !getValues(
                    'sharingFoldersRequiresOrganizerPermission'
                  ),
                },
              });
            }}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending || !isDirty,
      }}
    />
  );
}
